<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
} from 'vue';
import { isAxiosError } from 'axios';
import {
  GirderFileManager, GirderMarkdown,
} from '@girder/components/src';
import RunPipelineMenu from 'dive-common/components/RunPipelineMenu.vue';
import TrainingSplitMenu from 'dive-common/components/TrainingSplitMenu.vue';
import type { SubType } from 'dive-common/apispec';
import { isMultiCamTrainingTarget } from 'dive-common/multicamDisplay';
import { getMultiCamCameraCount } from 'dive-common/pipelineMenuFilters';
import { webExcludedPipelineTerms } from 'dive-common/constants';
import { usePrompt } from 'dive-common/vue-utilities/prompt-service';
import { useFolderDatasets } from '../useFolderDatasets';
import { isGirderModel } from '../store/types';
import { useConfig } from '../store/useConfig';
import { useJobs } from '../store/useJobs';
import { useLocation } from '../store/useLocation';
import RunTrainingMenu from './RunTrainingMenu.vue';

import { deleteResources } from '../api';
import Export from './Export.vue';
import Upload from './Upload.vue';
import DataDetails from './DataDetails.vue';
import Clone from './Clone.vue';
import ShareTab from './ShareTab.vue';
import eventBus from '../eventBus';

const buttonOptions = {
  block: true,
  left: true,
  depressed: true,
  color: 'primary',
  class: ['d-flex', 'justify-start'],
};

const menuOptions = {
  offsetX: true,
  right: true,
  nudgeRight: 8,
};

export default defineComponent({
  name: 'Home',
  components: {
    Clone,
    DataDetails,
    Export,
    GirderFileManager,
    GirderMarkdown,
    Upload,
    RunPipelineMenu,
    RunTrainingMenu,
    ShareTab,
    TrainingSplitMenu,
  },
  // everything below needs to be refactored to composition-api
  inject: ['girderRest'],
  setup() {
    const loading = ref(false);
    const { prompt } = usePrompt();
    const {
      location, selected, locationIsViameFolder, setSelected,
    } = useLocation();
    const {
      pipelinesEnabled,
      trainingEnabled,
      jobsDisabled,
      jobsDisabledMessage,
    } = useConfig();
    const jobs = useJobs();

    const clearSelected = () => {
      setSelected([]);
    };

    const folderSelection = computed(() => (
      locationIsViameFolder.value && location.value && isGirderModel(location.value)
        ? [location.value]
        : selected.value.filter((item) => item._modelType === 'folder')
    ));
    const selectedRootFolderIds = computed(() => folderSelection.value.map(({ _id }) => _id));
    const {
      datasets: selectedViameFolders,
      loading: resolvingFolders,
      error: folderSelectionError,
      refresh: refreshFolderDatasets,
    } = useFolderDatasets(folderSelection);

    const onTrainingSplitSaved = () => {
      refreshFolderDatasets();
      eventBus.$emit('refresh-data-browser');
    };
    const includesContainerFolders = computed(() => folderSelection.value.some(
      (item) => !item.meta?.annotate,
    ));
    const cloneDatasetId = computed(() => (
      folderSelection.value.length === 1 && folderSelection.value[0].meta?.annotate
        ? folderSelection.value[0]._id : null
    ));
    const runningPipelines = computed(() => selectedViameFolders.value
      .filter((item) => jobs.getDatasetRunningState(item._id)).map((item) => item._id));

    const selectedViameFolderIds = computed(() => selectedViameFolders.value.map(({ _id }) => _id));

    const selectedViameFolderNames = computed(() => selectedViameFolders.value.map(({ name }) => name));

    const pipelineTargetFolders = selectedViameFolders;

    const subTypeList = computed((): SubType[] => pipelineTargetFolders.value.map(
      (item) => item.meta?.subType ?? null,
    ));

    const cameraNumbers = computed(() => pipelineTargetFolders.value.map(
      (item) => getMultiCamCameraCount(item.meta),
    ));

    const datasetTypeList = computed(() => pipelineTargetFolders.value.map(
      (item) => item.meta?.type ?? null,
    ));

    const trainingSplits = computed(() => pipelineTargetFolders.value.map(
      (item) => item.meta?.trainingSplit ?? null,
    ));

    const selectedFileIds = computed(() => selected.value.filter(
      (element) => element._modelType === 'item',
    ).map(({ _id }) => _id));

    const includesLargeImage = computed(() => (pipelineTargetFolders.value.filter(
      ({ meta }) => meta && meta.type === 'large-image',
    )).length > 0);

    const includesMultiCamDataset = computed(() => isMultiCamTrainingTarget(
      pipelineTargetFolders.value,
      locationIsViameFolder.value && isGirderModel(location.value)
        ? location.value
        : null,
    ));

    const locationInputs = selectedViameFolderIds;
    const locationInputNames = selectedViameFolderNames;

    const selectedDescription = computed(() => (location.value as { description?: string } | null)?.description);

    return {
      // data
      buttonOptions,
      menuOptions,
      loading,
      location,
      selected,
      locationIsViameFolder,
      pipelinesEnabled,
      trainingEnabled,
      jobsDisabled,
      jobsDisabledMessage,
      runningPipelines,
      selectedViameFolderIds,
      selectedViameFolderNames,
      subTypeList,
      cameraNumbers,
      datasetTypeList,
      trainingSplits,
      selectedFileIds,
      includesLargeImage,
      includesMultiCamDataset,
      locationInputs,
      locationInputNames,
      selectedDescription,
      resolvingFolders,
      folderSelectionError,
      includesContainerFolders,
      cloneDatasetId,
      selectedRootFolderIds,
      onTrainingSplitSaved,
      // methods
      prompt,
      clearSelected,
      eventBus,
      webExcludedPipelineTerms,
    };
  },
  methods: {
    scoreSelection() {
      this.$router.push({
        name: 'scoring',
        query: { datasetIds: this.selectedViameFolderIds.join(',') },
      });
    },
    reviewSelection() {
      this.$router.push({
        name: 'review',
        query: { datasetIds: this.selectedViameFolderIds.join(',') },
      });
    },
    async deleteSelection() {
      const result = await this.prompt({
        title: 'Confirm',
        text: 'Do you want to delete selected items?',
        confirm: true,
      });
      if (!result) {
        return;
      }
      try {
        this.loading = true;
        await deleteResources(this.selected);
        eventBus.$emit('refresh-data-browser');
        this.clearSelected();
      } catch (err) {
        let text = 'Unable to delete resource(s)';
        if (isAxiosError(err) && err.response?.status === 403) {
          text = 'You do not have permission to delete selected resource(s).';
        }
        this.prompt({
          title: 'Delete Failed',
          text,
          positiveButton: 'OK',
        });
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>

<template>
  <div>
    <v-progress-linear
      :indeterminate="loading"
      height="6"
      :style="{ visibility: loading ? 'visible' : 'hidden' }"
    />
    <v-container
      fill-height
      :fluid="$vuetify.breakpoint.mdAndDown"
    >
      <v-row
        class="fill-height nowraptable"
      >
        <v-col cols="3">
          <DataDetails
            :value="selected.length ? selected : [location]"
          >
            <template #actions>
              <div class="pa-2 folder-actions">
                <Clone
                  v-bind="{ buttonOptions, menuOptions }"
                  :dataset-id="cloneDatasetId"
                />
                <div v-if="resolvingFolders" role="status">
                  Loading sequences…
                </div>
                <v-alert v-else-if="folderSelectionError" type="error" dense>
                  {{ folderSelectionError }}
                </v-alert>
                <div v-else-if="includesContainerFolders" role="status">
                  {{ locationInputs.length }} sequence(s) in the selected folders
                  (including subfolders).
                </div>
                <run-pipeline-menu
                  v-if="pipelinesEnabled && !resolvingFolders && !folderSelectionError"
                  v-bind="{
                    buttonOptions:
                      { ...buttonOptions, disabled: includesLargeImage },
                    menuOptions,
                    subTypeList,
                    cameraNumbers,
                    typeList: datasetTypeList,
                  }"
                  :selected-dataset-ids="locationInputs"
                  :selected-dataset-name="locationInputNames"
                  :running-pipelines="runningPipelines"
                  :exclude-pipeline-terms="webExcludedPipelineTerms"
                  :jobs-disabled="jobsDisabled"
                  :jobs-disabled-message="jobsDisabledMessage"
                />
                <run-training-menu
                  v-if="trainingEnabled && !resolvingFolders && !folderSelectionError"
                  v-bind="{
                    buttonOptions:
                      { ...buttonOptions, disabled: includesLargeImage || includesMultiCamDataset },
                    menuOptions,
                  }"
                  :selected-dataset-ids="locationInputs"
                  :dataset-splits="trainingSplits"
                />
                <v-btn
                  v-if="selectedViameFolderIds.length > 0"
                  v-bind="buttonOptions"
                  @click="reviewSelection"
                >
                  <v-icon>
                    mdi-view-grid-outline
                  </v-icon>
                  <span class="pl-1">
                    Review
                  </span>
                </v-btn>
                <v-btn
                  v-if="pipelinesEnabled && selectedViameFolderIds.length > 0"
                  v-bind="buttonOptions"
                  @click="scoreSelection"
                >
                  <v-icon>
                    mdi-chart-box-outline
                  </v-icon>
                  <span class="pl-1">
                    Score
                  </span>
                </v-btn>
                <training-split-menu
                  v-if="trainingEnabled && selectedRootFolderIds.length > 0"
                  v-bind="{ buttonOptions, menuOptions }"
                  :root-folder-ids="selectedRootFolderIds"
                  @saved="onTrainingSplitSaved"
                />
                <export
                  v-if="!resolvingFolders && !folderSelectionError"
                  v-bind="{ buttonOptions, menuOptions }"
                  :dataset-ids="locationInputs"
                  :file-ids="selectedFileIds"
                />
                <v-btn
                  :disabled="!selected.length"
                  v-bind="{ ...buttonOptions }"
                  color="error"
                  @click="deleteSelection"
                >
                  <v-icon>
                    mdi-delete
                  </v-icon>
                  <span class="pl-1">
                    Delete
                  </span>
                </v-btn>
              </div>
            </template>
          </DataDetails>
        </v-col>
        <v-col :cols="9">
          <v-toolbar
            dense
            class="mb-4"
            rounded
          >
            <ShareTab
              :value="0"
            />
          </v-toolbar>
          <router-view />
          <v-card
            v-if="selectedDescription"
            class="my-4"
          >
            <GirderMarkdown
              :text="selectedDescription"
              class="pa-3"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<style lang='scss'>
.nowraptable table thead tr th .row {
  flex-wrap: nowrap;
}

.folder-actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
